import { Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import StateCell from '../../components/cells/StateCell';
import DailyTrucksSection from '../../components/domain/DailyTrucksSection';
import ReportItemCard from '../../components/domain/ReportItemCard';
import Header from '../../components/layout/Header';
import PrimaryBox from '../../components/layout/PrimaryBox';
import ScrollPageContainer from '../../components/layout/ScrollPageContainer';
import ActivityResourceTransferModal from '../../components/modals/operations/validation/ActivityResourceTransferModal';
import ManageValidationDailyActivityModal from '../../components/modals/operations/validation/ManageDailyActivityModal';
import ManageValidationDailyMachineModal from '../../components/modals/operations/validation/ManageDailyMachineModal';
import ManageValidationDailyObservationsModal from '../../components/modals/operations/validation/ManageDailyObservationsModal';
import ManageValidationDailyRentalModal from '../../components/modals/operations/validation/ManageDailyRentalModal';
import ManageValidationDailySubContractorModal from '../../components/modals/operations/validation/ManageDailySubContractorModal';
import ManageValidationDailySupplyModal from '../../components/modals/operations/validation/ManageDailySupplyModal';
import ManageValidationDailyTempWorkerModal from '../../components/modals/operations/validation/ManageDailyTempWorkerModal';
import ManageValidationDailyToolModal from '../../components/modals/operations/validation/ManageDailyToolModal';
import ManageValidationDailyWasteModal from '../../components/modals/operations/validation/ManageDailyWasteModal';
import DisplayBox from '../../components/ui/DisplayBox';
import IconButton from '../../components/ui/IconButton';
import LabelValue from '../../components/ui/LabelValue';
import QuantityInput from '../../components/ui/QuantityInput';
import QuantityToggleGroup from '../../components/ui/QuantityToggleGroup';
import SectionCard from '../../components/ui/SectionCard';
import {
    activityStateEnum,
    activityTypeEnum,
    getMaterialStateLabel,
    getSupplyUnitLabel,
    localStorageKeys,
    materialStateEnum,
    permissionLevelEnum,
    stepModeEnum,
    systemConfigKeyEnum,
    wasteDirectionEnum,
} from '../../constants';
import { getActivityStateColor, getMaterialStateColor } from '../../constants/colors';
import { getActivityStateLabel, getWasteDirectionLabel } from '../../constants/labels';
import { useDeleteActivityMutation, useUpdateActivityMutation, useValidateSiteActivitiesMutation } from '../../hooks/mutations/useActivityMutation';
import { useDailyMachineMutation, useDeleteDailyMachineMutation } from '../../hooks/mutations/useDailyMachineMutation';
import { useDailyRentalMutation, useDeleteDailyRentalMutation } from '../../hooks/mutations/useDailyRentalMutation';
import { useDailySubContractorMutation, useDeleteDailySubContractorMutation } from '../../hooks/mutations/useDailySubContractorMutation';
import { useDeleteDailySupplyMutation } from '../../hooks/mutations/useDailySupplyMutation';
import { useDailyTempWorkerMutation, useDeleteDailyTempWorkerMutation } from '../../hooks/mutations/useDailyTempWorkerMutation';
import { useDailyToolMutation, useDeleteDailyToolMutation } from '../../hooks/mutations/useDailyToolMutation';
import { useDeleteDailyWasteMutation, useUpdateDailyWasteMutation } from '../../hooks/mutations/useDailyWasteMutation';
import { useUpsertSiteDailyReportMutation } from '../../hooks/mutations/useSiteDailyReportMutation';
import { useSiteReportQuery, useUsersMissingReportsQuery } from '../../hooks/queries/useActivitiesQuery';
import { useSiteDailyReportBySiteAndDateQuery } from '../../hooks/queries/useSiteDailyReportsQuery';
import { useAuth } from '../../hooks/useAuth';
import { useBoolean } from '../../hooks/useBoolean';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { useTheme } from '../../hooks/useTheme';
import { DataItem, SiteReport } from '../../services/apiSLTP/dailyReports/activities';
import { capitalizeFirstLetter, formatName } from '../../utils/capitalize';
import { formatTime, getTomorrowDate } from '../../utils/dates';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';
import { formatNumber } from '../../utils/numbers';

const SiteValidationPage: React.FC = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const { mode, muiTheme } = useTheme();
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date(getItemFromLocalStorage(localStorageKeys.DATE) || getTomorrowDate()));

    const {
        data: dataSiteReport,
        isLoading: isSiteReportLoading,
        refetch: refetchSiteReport,
    } = useSiteReportQuery({
        siteId: siteId || '',
        stepMode: stepModeEnum.VALIDATE,
        queries: { date: currentDate.toISOString() },
    });

    const { data: dataUsersMissingReports } = useUsersMissingReportsQuery({ date: currentDate.toISOString(), siteId: siteId || '' });

    const {
        data: dataSiteDailyReport,
        isLoading: isSiteDailyReportLoading,
        refetch: refetchSiteDailyReport,
    } = useSiteDailyReportBySiteAndDateQuery({ siteId: siteId || '', date: currentDate.toISOString() });

    usePageTitle(capitalizeFirstLetter(t('validation')) + ' ' + (dataSiteReport?.site.name || ''));

    const { mutate: validateSite, isLoading: isValidateSiteLoading } = useValidateSiteActivitiesMutation({ onSuccess: () => refetchSiteReport() });

    const { mutate: updateActivity } = useUpdateActivityMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteActivity } = useDeleteActivityMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: updateDailyMachine } = useDailyMachineMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteDailyMachine } = useDeleteDailyMachineMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: updateDailyTool } = useDailyToolMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteDailyTool } = useDeleteDailyToolMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: updateDailyRental } = useDailyRentalMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteDailyRental } = useDeleteDailyRentalMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: updateDailyTempWorker } = useDailyTempWorkerMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteDailyTempWorker } = useDeleteDailyTempWorkerMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: updateDailySubContractor } = useDailySubContractorMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteDailySubContractor } = useDeleteDailySubContractorMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: deleteDailySupply } = useDeleteDailySupplyMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: updateDailyWaste } = useUpdateDailyWasteMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });
    const { mutate: deleteDailyWaste } = useDeleteDailyWasteMutation({ onSuccess: () => refetchSiteReport({ cancelRefetch: true }) });

    const { mutate: upsertSiteDailyReport } = useUpsertSiteDailyReportMutation({ onSuccess: () => refetchSiteDailyReport() });

    const handleDateChange = (newDate: string) => {
        setCurrentDate(new Date(newDate));
        storeItemToLocalStorage(localStorageKeys.DATE, newDate);
    };

    const activityModal = useBoolean(false);
    const activityTransferModal = useBoolean(false);
    const dailyMachineModal = useBoolean(false);
    const dailyToolModal = useBoolean(false);
    const dailyRentalModal = useBoolean(false);
    const dailyTempWorkerModal = useBoolean(false);
    const dailySubContractorModal = useBoolean(false);
    const dailySupplyModal = useBoolean(false);
    const dailyWasteModal = useBoolean(false);
    const globalObservationsModal = useBoolean(false);

    const [selectedActivity, setSelectedActivity] = useState<SiteReport['activities'][0] | null>(null);
    const [selectedDailyMachine, setSelectedDailyMachine] = useState<SiteReport['dailyMachines'][0] | null>(null);
    const [selectedDailyTool, setSelectedDailyTool] = useState<SiteReport['dailyTools'][0] | null>(null);
    const [selectedDailyRental, setSelectedDailyRental] = useState<SiteReport['dailyRentals'][0] | null>(null);
    const [selectedDailyTempWorker, setSelectedDailyTempWorker] = useState<SiteReport['dailyTempWorkers'][0] | null>(null);
    const [selectedDailySubContractor, setSelectedDailySubContractor] = useState<SiteReport['dailySubContractors'][0] | null>(null);
    const [selectedDailySupply, setSelectedDailySupply] = useState<SiteReport['dailySupplies'][0] | null>(null);
    const [selectedDailyWaste, setSelectedDailyWaste] = useState<SiteReport['dailyWastes'][0] | null>(null);

    const disabledWithoutSemiValidated =
        (dataSiteReport?.site.conducId?._id !== user?._id &&
            dataSiteReport?.site.assistantConducId?._id !== user?._id &&
            (user?.profileId.permsLevel ?? Infinity) > permissionLevelEnum.PLANNING) ||
        dataSiteReport?.activities.every((a) => a.activityState === activityStateEnum.VALIDATED || a.activityState === activityStateEnum.ENTERED);

    const disabled = disabledWithoutSemiValidated || dataSiteReport?.activities.every((a) => a.activityState === activityStateEnum.SEMIVALIDATED);

    const canShowTransferIcon = (activityId: string, dataSiteReport: SiteReport): boolean => {
        const hasDailyItems = [
            dataSiteReport.dailyMachines,
            dataSiteReport.dailyTools,
            dataSiteReport.dailyRentals,
            dataSiteReport.dailySupplies,
            dataSiteReport.dailyTempWorkers,
            dataSiteReport.dailyTrucks,
        ].some((arr) => arr?.some((item) => item.activityId === activityId));

        const hasTruckRides = [dataSiteReport.dailyManagerTruckRides, dataSiteReport.dailyDriversTruckRides].some((arr) =>
            arr?.some((item) => item.activityId === activityId)
        );

        return hasDailyItems && !hasTruckRides;
    };

    const canDeleteActivity = (activityId: string, dataSiteReport: SiteReport): boolean => {
        const hasDailyItems = [
            dataSiteReport.dailyMachines,
            dataSiteReport.dailyTools,
            dataSiteReport.dailyRentals,
            dataSiteReport.dailySupplies,
            dataSiteReport.dailyTempWorkers,
            dataSiteReport.dailyTrucks,
            dataSiteReport.dailyManagerTruckRides,
            dataSiteReport.dailyDriversTruckRides,
        ].some((arr) => arr?.some((item) => item.activityId === activityId));

        return !hasDailyItems;
    };

    const calculateWorkDurationMinusBreak = (startTime: string, endTime: string, isDriver: boolean): number => {
        const breakTimeKey = isDriver ? systemConfigKeyEnum.DEFAULT_DRIVER_BREAK_TIME : systemConfigKeyEnum.DEFAULT_WORKER_BREAK_TIME;
        const breakTime = Number(getConfigValue(breakTimeKey)) ?? 1;
        const start = new Date(startTime);
        const end = new Date(endTime);

        const diffMs = end.getTime() - start.getTime();
        const diffMinusBreakMs = diffMs - breakTime * 60 * 60 * 1000;
        const hoursDecimal = diffMinusBreakMs / (1000 * 60 * 60);

        return Math.round(hoursDecimal * 100) / 100;
    };

    const calculateWorkTimeLimits = (activity: DataItem): { minHours: number; maxHours: number } => {
        const isDriver = Boolean(activity.reportId?.truckId);

        const workTimeKey = isDriver ? systemConfigKeyEnum.DEFAULT_DRIVER_WORK_TIME : systemConfigKeyEnum.DEFAULT_WORKER_WORK_TIME;

        const breakTimeKey = isDriver ? systemConfigKeyEnum.DEFAULT_DRIVER_BREAK_TIME : systemConfigKeyEnum.DEFAULT_WORKER_BREAK_TIME;

        const bufferKey = systemConfigKeyEnum.OVERTIME_BUFFER_HOURS;

        const workTime = Number(getConfigValue(workTimeKey)) ?? 8;
        const breakTime = Number(getConfigValue(breakTimeKey)) ?? 1;
        const buffer = Number(getConfigValue(bufferKey)) ?? 0;

        const minHours = workTime - buffer;
        const maxHours = workTime + buffer;

        return { minHours, maxHours };
    };

    const decimalHoursToTimeString = (decimalHours: number): string => {
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[
                    { label: capitalizeFirstLetter(t('validation')), link: '/operations/validation' },
                    { label: dataSiteReport?.site.name || '' },
                ]}
                subTitle={capitalizeFirstLetter(t('validation')) + ' ' + (dataSiteReport?.site.name || '')}
                initialDate={currentDate}
                onDateChange={(newDate) => handleDateChange(newDate.toISOString())}
            >
                <Typography variant="h5" color="success" fontWeight={'bold'} noWrap>
                    <CountUp
                        end={
                            (dataSiteReport?.activities.reduce((acc, activity) => {
                                return (
                                    acc +
                                    ((activity.unitUserPrice ?? activity.reportId.userId.price ?? activity.reportId.userId.jobId.price) +
                                        (activity.unitVehiclePrice ??
                                            activity.reportId.vehicleId?.price ??
                                            activity.reportId.vehicleId?.vehicleTypeId.price ??
                                            0) +
                                        (activity.reportId.isLongTrip
                                            ? (activity.unitLongTripPrice ?? getConfigValue(systemConfigKeyEnum.LARGE_JOURNEY_PRICE) ?? 0)
                                            : 0)) *
                                        (activity.quantity ?? 0)
                                );
                            }, 0) ?? 0) +
                            (dataSiteReport?.dailyMachines.reduce((acc, machine) => {
                                return (
                                    acc +
                                    (machine.unitPrice ?? machine.machineId.price ?? machine.machineId.machineTypeId.price ?? 0) *
                                        (machine.quantity ?? 0)
                                );
                            }, 0) ?? 0) +
                            (dataSiteReport?.dailyTools.reduce((acc, tool) => {
                                return acc + (tool.unitPrice ?? tool.toolId.price ?? tool.toolId.toolTypeId.price ?? 0) * (tool.quantity ?? 0);
                            }, 0) ?? 0) +
                            (dataSiteReport?.dailyRentals.reduce((acc, rental) => {
                                return acc + (rental.unitPrice ?? 0) * (rental.quantity ?? 0);
                            }, 0) ?? 0) +
                            (dataSiteReport?.dailyTempWorkers.reduce((acc, tempWorker) => {
                                return acc + (tempWorker.unitPrice ?? 0) * (tempWorker.durationHours ?? 0);
                            }, 0) ?? 0) +
                            (dataSiteReport?.dailySupplies.reduce((acc, supply) => {
                                return acc + (supply.unitPrice ?? 0) * (supply.quantity ?? 0);
                            }, 0) ?? 0) +
                            (dataSiteReport?.dailyTrucks
                                .filter((truck) => !truck.truckId.truckCompanyId.isOwnCompany)
                                .reduce((acc, truck) => {
                                    return acc + (truck.unitPrice ?? 0) * (truck.quantity ?? 0);
                                }, 0) ?? 0) +
                            (dataSiteReport?.dailyTrucks
                                .filter((truck) => !!truck.truckId.truckCompanyId.isOwnCompany)
                                .reduce((acc, truck) => {
                                    return (
                                        acc + (truck.unitPrice ?? truck.truckId.price ?? truck.truckId.truckTypeId.price ?? 0) * (truck.quantity ?? 0)
                                    );
                                }, 0) ?? 0) +
                            (dataSiteReport?.dailyWastes.reduce((acc, dailyWaste) => {
                                return acc + (dailyWaste.unitPrice ?? 0) * (dailyWaste.quantity ?? 0);
                            }, 0) ?? 0)
                        }
                        duration={2}
                        separator=" "
                        decimals={2}
                        decimal=","
                        preserveValue
                        suffix={` ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                    />
                </Typography>
                {dataSiteReport?.activities.some((a) => a.activityState > activityStateEnum.VALIDATED) && (
                    <Button
                        variant="contained"
                        color="success"
                        disabled={disabledWithoutSemiValidated || dataUsersMissingReports?.length !== 0}
                        onClick={() => {
                            if (window.confirm(capitalizeFirstLetter(t('confirmValidateSite')))) {
                                validateSite({
                                    siteId: siteId || '',
                                    body: { date: currentDate.toISOString(), activityState: activityStateEnum.VALIDATED },
                                });
                            }
                        }}
                        loading={isValidateSiteLoading}
                    >
                        {capitalizeFirstLetter(t('validate'))}
                    </Button>
                )}
                {!!dataSiteReport?.activities.length &&
                    dataSiteReport?.activities.every((a) => a.activityState === activityStateEnum.VALIDATED) &&
                    user &&
                    user.profileId.permsLevel <= permissionLevelEnum.SUPERVISION && (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => {
                                if (window.confirm(capitalizeFirstLetter(t('confirmMarkAsEntered')))) {
                                    validateSite({
                                        siteId: siteId || '',
                                        body: { date: currentDate.toISOString(), activityState: activityStateEnum.ENTERED },
                                    });
                                }
                            }}
                            loading={isValidateSiteLoading}
                        >
                            {capitalizeFirstLetter(t('markAsEntered'))}
                        </Button>
                    )}

                {!!dataSiteReport?.activities.length && (
                    <>
                        {dataSiteReport?.activities.every((a) => a.activityState === activityStateEnum.VALIDATED) &&
                        !dataUsersMissingReports?.length ? (
                            <StateCell
                                text={getActivityStateLabel(activityStateEnum.VALIDATED)}
                                stateColor={getActivityStateColor(activityStateEnum.VALIDATED)}
                            />
                        ) : dataSiteReport?.activities.every((a) => a.activityState === activityStateEnum.ENTERED) &&
                          !dataUsersMissingReports?.length ? (
                            <StateCell
                                text={getActivityStateLabel(activityStateEnum.ENTERED)}
                                stateColor={getActivityStateColor(activityStateEnum.ENTERED)}
                            />
                        ) : !!dataUsersMissingReports?.length ? (
                            <StateCell
                                text={getActivityStateLabel(activityStateEnum.REPORT)}
                                stateColor={getActivityStateColor(activityStateEnum.REPORT)}
                            />
                        ) : (
                            <StateCell
                                text={getActivityStateLabel(activityStateEnum.VALIDATION)}
                                stateColor={getActivityStateColor(activityStateEnum.VALIDATION)}
                            />
                        )}
                    </>
                )}
            </Header>
            <ScrollPageContainer loading={isSiteReportLoading}>
                <SectionCard
                    title={capitalizeFirstLetter(t('workers'))}
                    onAdd={() => {
                        activityModal.setTrue();
                        setSelectedActivity(null);
                    }}
                    disabled={disabled}
                    iconTitleName="peopleAlt"
                    totalPrice={
                        !!dataSiteReport?.activities.length
                            ? dataSiteReport?.activities.reduce((acc, activity) => {
                                  return (
                                      acc +
                                      ((activity.unitUserPrice ?? activity.reportId.userId.price ?? activity.reportId.userId.jobId.price) +
                                          (activity.unitVehiclePrice ??
                                              activity.reportId.vehicleId?.price ??
                                              activity.reportId.vehicleId?.vehicleTypeId.price ??
                                              0) +
                                          (activity.reportId.isLongTrip
                                              ? (activity.unitLongTripPrice ?? getConfigValue(systemConfigKeyEnum.LARGE_JOURNEY_PRICE) ?? 0)
                                              : 0)) *
                                          (activity.quantity ?? 0)
                                  );
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.activities
                        .sort((a, b) => {
                            if (a.reportId.userId.jobId.displayOrder !== b.reportId.userId.jobId.displayOrder) {
                                return (a.reportId.userId.jobId.displayOrder ?? Infinity) - (b.reportId.userId.jobId.displayOrder ?? Infinity);
                            }
                            return a.reportId.userId.firstName.localeCompare(b.reportId.userId.firstName);
                        })
                        .map((activity) => (
                            <ReportItemCard
                                key={activity._id}
                                title={
                                    formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName) +
                                    (activity.isTruckManager ? ' 🎗️' : '')
                                }
                                onEdit={() => {
                                    activityModal.setTrue();
                                    setSelectedActivity(activity);
                                }}
                                onDelete={() => {
                                    deleteActivity({ id: activity._id });
                                }}
                                disabledOnDelete={!canDeleteActivity(activity._id, dataSiteReport)}
                                disabled={disabled}
                                price={
                                    ((activity.unitUserPrice ?? activity.reportId.userId.price ?? activity.reportId.userId.jobId.price) +
                                        (activity.unitVehiclePrice ??
                                            activity.reportId.vehicleId?.price ??
                                            activity.reportId.vehicleId?.vehicleTypeId.price ??
                                            0) +
                                        (activity.reportId.isLongTrip
                                            ? (activity.unitLongTripPrice ?? getConfigValue(systemConfigKeyEnum.LARGE_JOURNEY_PRICE) ?? 0)
                                            : 0)) *
                                    (activity.quantity ?? 0)
                                }
                                unitPrice={
                                    (activity.unitUserPrice ?? activity.reportId.userId.price ?? activity.reportId.userId.jobId.price) +
                                    (activity.unitVehiclePrice ??
                                        activity.reportId.vehicleId?.price ??
                                        activity.reportId.vehicleId?.vehicleTypeId.price ??
                                        0)
                                }
                                extraActions={
                                    <>
                                        <IconButton
                                            iconName="transferWithinAStation"
                                            onClick={() => {
                                                activityTransferModal.setTrue();
                                                setSelectedActivity(activity);
                                            }}
                                            disabled={!canShowTransferIcon(activity._id, dataSiteReport)}
                                        />
                                    </>
                                }
                                majorAction={
                                    <>
                                        <QuantityToggleGroup
                                            value={activity.quantity}
                                            onChange={(value) => updateActivity({ id: activity._id, body: { quantity: value } })}
                                            disabled={disabled}
                                        />
                                    </>
                                }
                                extraInfo={
                                    <>
                                        <Typography
                                            variant="body2"
                                            color={
                                                activity.reportId.workStartTime &&
                                                activity.reportId.workEndTime &&
                                                (calculateWorkDurationMinusBreak(
                                                    activity.reportId.workStartTime,
                                                    activity.reportId.workEndTime,
                                                    Boolean(activity.reportId?.truckId)
                                                ) < calculateWorkTimeLimits(activity).minHours ||
                                                    calculateWorkDurationMinusBreak(
                                                        activity.reportId.workStartTime,
                                                        activity.reportId.workEndTime,
                                                        Boolean(activity.reportId?.truckId)
                                                    ) > calculateWorkTimeLimits(activity).maxHours)
                                                    ? 'error'
                                                    : 'textPrimary'
                                            }
                                        >
                                            {activity.reportId.workStartTime && activity.reportId.workEndTime
                                                ? `(${capitalizeFirstLetter(t('declaredDaytimeWorkedTime'))}${t('separator.colon')}${decimalHoursToTimeString(calculateWorkDurationMinusBreak(activity.reportId.workStartTime, activity.reportId.workEndTime, Boolean(activity.reportId?.truckId)))})`
                                                : ''}
                                        </Typography>
                                        <Typography variant="body2" color={'info'}>
                                            {activity.reportId.extraHours
                                                ? `(${capitalizeFirstLetter(t('validatedOvertime'))}${t('separator.colon')}${decimalHoursToTimeString(activity.reportId.extraHours)})`
                                                : ''}
                                        </Typography>
                                        <Typography variant="body2" color="warning">
                                            {activity.reportId.isLongTrip ? `(GD)` : ''}
                                        </Typography>
                                    </>
                                }
                            >
                                {!!activity.reportId.workStartTime && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('declaredStartDayTime'))}
                                        value={formatTime(new Date(activity.reportId.workStartTime))}
                                    />
                                )}
                                {!!activity.reportId.workEndTime && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('declaredEndDayTime'))}
                                        value={formatTime(new Date(activity.reportId.workEndTime))}
                                    />
                                )}
                                {!!activity.reportId.overTimeValidatedBy && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('workTimeValidator'))}
                                        value={formatName(
                                            activity.reportId.overTimeValidatedBy.firstName,
                                            activity.reportId.overTimeValidatedBy.lastName
                                        )}
                                    />
                                )}
                                {!!activity.reportId.extraHours && (
                                    <LabelValue title={capitalizeFirstLetter(t('validatedDayOverTime'))} value={activity.reportId.extraHours} />
                                )}
                                <LabelValue
                                    title={capitalizeFirstLetter(t('userUnitPrice'))}
                                    value={`${activity.reportId.userId.price ?? activity.reportId.userId.jobId.price} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                />
                                {!!activity.reportId.isLongTrip && (
                                    <LabelValue title={capitalizeFirstLetter(t('longTrip'))} value={capitalizeFirstLetter(t('yes'))} />
                                )}
                                {!!activity.reportId.isLongTrip && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('longTripPrice'))}
                                        value={`${getConfigValue(systemConfigKeyEnum.LARGE_JOURNEY_PRICE) ?? 0} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                    />
                                )}
                                {!!activity.reportId.vehicleId?.designation && (
                                    <LabelValue title={capitalizeFirstLetter(t('vehicle'))} value={activity.reportId.vehicleId.designation} />
                                )}
                                {!!activity.reportId.vehicleId?.designation && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('vehicleUnitPrice'))}
                                        value={`${activity.reportId.vehicleId.price ?? activity.reportId.vehicleId.vehicleTypeId.price} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                    />
                                )}
                                <LabelValue title={capitalizeFirstLetter(t('activity'))} value={activity.activityTypeId.designation} />
                                {activity.isTruckManager && (
                                    <LabelValue title={capitalizeFirstLetter(t('truckManager'))} value={capitalizeFirstLetter(t('yes'))} />
                                )}
                                {!!activity.observationsWorker && (
                                    <LabelValue title={capitalizeFirstLetter(t('observations'))} value={activity.observationsWorker} />
                                )}
                            </ReportItemCard>
                        ))}
                    {dataUsersMissingReports
                        ?.sort((a, b) => a.firstName.localeCompare(b.firstName))
                        .map((user) => (
                            <ReportItemCard
                                key={user._id}
                                title={formatName(user.firstName, user.lastName)}
                                extraInfo={
                                    <Typography color="error">
                                        {capitalizeFirstLetter(t('missingReport'))}
                                        {user.isSpecial ? ' Soumission automatique à 15h' : ''}
                                    </Typography>
                                }
                                disabled
                                hidePrice
                                hideMajorAction
                                error
                            />
                        ))}
                </SectionCard>
                {/* Machines */}
                <SectionCard
                    title={capitalizeFirstLetter(t('machines'))}
                    iconTitleName="precisionManufacturing"
                    onAdd={() => {
                        dailyMachineModal.setTrue();
                        setSelectedDailyMachine(null);
                    }}
                    disabled={disabled}
                    totalPrice={
                        !!dataSiteReport?.dailyMachines.length
                            ? dataSiteReport?.dailyMachines.reduce((acc, dailyMachine) => {
                                  return (
                                      acc +
                                      (dailyMachine.unitPrice ?? dailyMachine.machineId.price ?? dailyMachine.machineId.machineTypeId.price) *
                                          (dailyMachine.quantity ?? 0)
                                  );
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.dailyMachines
                        .sort((a, b) => {
                            const orderA = a.machineId.machineTypeId.displayOrder || 0;
                            const orderB = b.machineId.machineTypeId.displayOrder || 0;
                            if (orderA !== orderB) return orderA - orderB;
                            if (a.machineId.machineTypeId.designation !== b.machineId.machineTypeId.designation)
                                return a.machineId.machineTypeId.designation.localeCompare(b.machineId.machineTypeId.designation);
                            return a.machineId.designation.localeCompare(b.machineId.designation);
                        })
                        .map((dailyMachine) => (
                            <ReportItemCard
                                key={dailyMachine._id}
                                title={dailyMachine.machineId.designation}
                                onEdit={() => {
                                    dailyMachineModal.setTrue();
                                    setSelectedDailyMachine(dailyMachine);
                                }}
                                onDelete={() => deleteDailyMachine({ id: dailyMachine._id })}
                                price={
                                    (dailyMachine.unitPrice ?? dailyMachine.machineId.price ?? dailyMachine.machineId.machineTypeId.price) *
                                    (dailyMachine.quantity ?? 0)
                                }
                                unitPrice={dailyMachine.unitPrice ?? dailyMachine.machineId.price ?? dailyMachine.machineId.machineTypeId.price}
                                disabled={disabled}
                                majorAction={
                                    <>
                                        <QuantityToggleGroup
                                            value={dailyMachine.quantity}
                                            onChange={(value) => updateDailyMachine({ id: dailyMachine._id, body: { quantity: value } })}
                                            disabled={disabled}
                                        />
                                    </>
                                }
                                extraInfo={
                                    dailyMachine.state !== materialStateEnum.PRESENT ? (
                                        <StateCell
                                            stateColor={getMaterialStateColor(dailyMachine.state)}
                                            text={t(getMaterialStateLabel(dailyMachine.state))}
                                        />
                                    ) : undefined
                                }
                            >
                                <LabelValue
                                    title={capitalizeFirstLetter(t('activityOf'))}
                                    value={formatName(
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyMachine.activityId)?.reportId.userId
                                            .firstName || '',
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyMachine.activityId)?.reportId.userId
                                            .lastName || ''
                                    )}
                                />
                                <LabelValue
                                    title={capitalizeFirstLetter(t('unitPrice'))}
                                    value={`${dailyMachine.unitPrice ?? dailyMachine.machineId.price ?? dailyMachine.machineId.machineTypeId.price} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                />
                                <LabelValue
                                    title={capitalizeFirstLetter(t('state'))}
                                    value={capitalizeFirstLetter(t(getMaterialStateLabel(dailyMachine.state)))}
                                />
                                <LabelValue title={capitalizeFirstLetter(t('type'))} value={dailyMachine.machineId.machineTypeId.designation} />
                                <LabelValue title={capitalizeFirstLetter(t('codeProC'))} value={dailyMachine.machineId.codeProC} />
                            </ReportItemCard>
                        ))}
                </SectionCard>
                {/* Tools */}
                <SectionCard
                    title={capitalizeFirstLetter(t('tools'))}
                    iconTitleName="construction"
                    onAdd={() => {
                        dailyToolModal.setTrue();
                        setSelectedDailyTool(null);
                    }}
                    disabled={disabled}
                    totalPrice={
                        !!dataSiteReport?.dailyTools.length
                            ? dataSiteReport?.dailyTools.reduce((acc, dailyTool) => {
                                  return (
                                      acc +
                                      (dailyTool.unitPrice ?? dailyTool.toolId.price ?? dailyTool.toolId.toolTypeId.price) * (dailyTool.quantity ?? 0)
                                  );
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.dailyTools
                        .sort((a, b) => {
                            const orderA = a.toolId.toolTypeId.displayOrder || 0;
                            const orderB = b.toolId.toolTypeId.displayOrder || 0;
                            if (orderA !== orderB) return orderA - orderB;
                            if (a.toolId.toolTypeId.designation !== b.toolId.toolTypeId.designation)
                                return a.toolId.toolTypeId.designation.localeCompare(b.toolId.toolTypeId.designation);
                            return a.toolId.designation.localeCompare(b.toolId.designation);
                        })
                        .map((dailyTool) => (
                            <ReportItemCard
                                key={dailyTool._id}
                                title={dailyTool.toolId.designation}
                                onEdit={() => {
                                    dailyToolModal.setTrue();
                                    setSelectedDailyTool(dailyTool);
                                }}
                                onDelete={() => deleteDailyTool({ id: dailyTool._id })}
                                price={
                                    (dailyTool.unitPrice ?? dailyTool.toolId.price ?? dailyTool.toolId.toolTypeId.price) * (dailyTool.quantity ?? 0)
                                }
                                unitPrice={dailyTool.unitPrice ?? dailyTool.toolId.price ?? dailyTool.toolId.toolTypeId.price}
                                disabled={disabled}
                                majorAction={
                                    <>
                                        <QuantityToggleGroup
                                            value={dailyTool.quantity}
                                            onChange={(value) => updateDailyTool({ id: dailyTool._id, body: { quantity: value } })}
                                            disabled={disabled}
                                        />
                                    </>
                                }
                                extraInfo={
                                    dailyTool.state !== materialStateEnum.PRESENT ? (
                                        <StateCell
                                            stateColor={getMaterialStateColor(dailyTool.state)}
                                            text={t(getMaterialStateLabel(dailyTool.state))}
                                        />
                                    ) : undefined
                                }
                            >
                                <LabelValue
                                    title={capitalizeFirstLetter(t('activityOf'))}
                                    value={formatName(
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyTool.activityId)?.reportId.userId
                                            .firstName || '',
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyTool.activityId)?.reportId.userId
                                            .lastName || ''
                                    )}
                                />
                                <LabelValue
                                    title={capitalizeFirstLetter(t('unitPrice'))}
                                    value={`${dailyTool.unitPrice ?? dailyTool.toolId.price ?? dailyTool.toolId.toolTypeId.price} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                />
                                <LabelValue
                                    title={capitalizeFirstLetter(t('state'))}
                                    value={capitalizeFirstLetter(t(getMaterialStateLabel(dailyTool.state)))}
                                />
                                <LabelValue title={capitalizeFirstLetter(t('type'))} value={dailyTool.toolId.toolTypeId.designation} />
                                <LabelValue title={capitalizeFirstLetter(t('codeProC'))} value={dailyTool.toolId.codeProC} />
                            </ReportItemCard>
                        ))}
                </SectionCard>
                {/* Rentals */}
                <SectionCard
                    title={capitalizeFirstLetter(t('rentals'))}
                    iconTitleName="shoppingCart"
                    onAdd={() => {
                        dailyRentalModal.setTrue();
                        setSelectedDailyRental(null);
                    }}
                    disabled={disabledWithoutSemiValidated}
                    totalPrice={
                        !!dataSiteReport?.dailyRentals.length
                            ? dataSiteReport?.dailyRentals.reduce((acc, dailyRental) => {
                                  return acc + (dailyRental.unitPrice ?? 0) * (dailyRental.quantity ?? 0);
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.dailyRentals
                        .sort((a, b) => {
                            const orderA = a.rentalTypeId.displayOrder || 0;
                            const orderB = b.rentalTypeId.displayOrder || 0;
                            if (orderA !== orderB) return orderA - orderB;
                            if (a.rentalTypeId.designation !== b.rentalTypeId.designation)
                                return a.rentalTypeId.designation.localeCompare(b.rentalTypeId.designation);
                            return a.designation.localeCompare(b.designation);
                        })
                        .map((dailyRental) => (
                            <ReportItemCard
                                key={dailyRental._id}
                                title={`${dailyRental.designation} - ${dailyRental.rentalTypeId.designation}`}
                                onEdit={() => {
                                    dailyRentalModal.setTrue();
                                    setSelectedDailyRental(dailyRental);
                                }}
                                onDelete={() => deleteDailyRental({ id: dailyRental._id })}
                                price={
                                    dailyRental.unitPrice !== undefined && dailyRental.unitPrice !== null
                                        ? dailyRental.unitPrice * (dailyRental.quantity ?? 0)
                                        : undefined
                                }
                                unitPrice={dailyRental.unitPrice}
                                disabled={disabledWithoutSemiValidated}
                                majorAction={
                                    <>
                                        <QuantityToggleGroup
                                            value={dailyRental.quantity}
                                            onChange={(value) => updateDailyRental({ id: dailyRental._id, body: { quantity: value } })}
                                            options={[
                                                { value: 0, label: '0 j' },
                                                { value: 0.5, label: '0.5 j', disabled: true },
                                                { value: 1, label: '1 j' },
                                            ]}
                                            disabled={disabledWithoutSemiValidated}
                                        />
                                    </>
                                }
                                extraInfo={
                                    <>
                                        {dailyRental.state !== materialStateEnum.PRESENT ? (
                                            <StateCell
                                                stateColor={getMaterialStateColor(dailyRental.state)}
                                                text={t(getMaterialStateLabel(dailyRental.state))}
                                            />
                                        ) : undefined}
                                        {dailyRental.isExposedToWeather && '🌧️'}
                                    </>
                                }
                            >
                                <LabelValue
                                    title={capitalizeFirstLetter(t('activityOf'))}
                                    value={formatName(
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyRental.activityId)?.reportId.userId
                                            .firstName || '',
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyRental.activityId)?.reportId.userId
                                            .lastName || ''
                                    )}
                                />
                                {dailyRental.unitPrice !== undefined && dailyRental.unitPrice !== null && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('unitPrice'))}
                                        value={`${dailyRental.unitPrice} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                    />
                                )}
                                <LabelValue
                                    title={capitalizeFirstLetter(t('state'))}
                                    value={capitalizeFirstLetter(t(getMaterialStateLabel(dailyRental.state)))}
                                />
                                {!!dailyRental.rentalTypeId.codeProC && (
                                    <LabelValue title={capitalizeFirstLetter(t('companyCodeProC'))} value={dailyRental.rentalTypeId.codeProC} />
                                )}
                                {!!dailyRental.observations && (
                                    <LabelValue title={capitalizeFirstLetter(t('observations'))} value={dailyRental.observations} />
                                )}
                                {dailyRental.isExposedToWeather && (
                                    <LabelValue title={capitalizeFirstLetter(t('badWeather'))} value={capitalizeFirstLetter(t('yes'))} />
                                )}
                            </ReportItemCard>
                        ))}
                </SectionCard>
                {/* TempWorkers */}
                <SectionCard
                    title={capitalizeFirstLetter(t('tempWorkers'))}
                    iconTitleName="badge"
                    onAdd={() => {
                        dailyTempWorkerModal.setTrue();
                        setSelectedDailyTempWorker(null);
                    }}
                    disabled={disabledWithoutSemiValidated}
                    totalPrice={
                        !!dataSiteReport?.dailyTempWorkers.length
                            ? dataSiteReport?.dailyTempWorkers.reduce((acc, dailyTempWorker) => {
                                  return acc + (dailyTempWorker.unitPrice ?? 0) * (dailyTempWorker.durationHours ?? 0);
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.dailyTempWorkers
                        .sort((a, b) => {
                            const typeA = a.tempWorkerTypeId.designation;
                            const typeB = b.tempWorkerTypeId.designation;
                            if (typeA !== typeB) return typeA.localeCompare(typeB);
                            return (a.tempWorkerName || '').localeCompare(b.tempWorkerName || '');
                        })
                        .map((dailyTempWorker) => (
                            <ReportItemCard
                                key={dailyTempWorker._id}
                                title={`${dailyTempWorker.tempWorkerName || ''} - ${dailyTempWorker.tempWorkerTypeId.designation}`}
                                onEdit={() => {
                                    dailyTempWorkerModal.setTrue();
                                    setSelectedDailyTempWorker(dailyTempWorker);
                                }}
                                onDelete={() => deleteDailyTempWorker({ id: dailyTempWorker._id })}
                                disabled={disabledWithoutSemiValidated}
                                price={
                                    dailyTempWorker.unitPrice && dailyTempWorker.durationHours
                                        ? dailyTempWorker.unitPrice * dailyTempWorker.durationHours
                                        : undefined
                                }
                                unitPrice={dailyTempWorker.unitPrice}
                                majorAction={
                                    <QuantityInput
                                        value={dailyTempWorker.durationHours ?? 0}
                                        onChange={(newValue) => updateDailyTempWorker({ id: dailyTempWorker._id, body: { durationHours: newValue } })}
                                        unit="H"
                                        min={0}
                                        max={24}
                                        step={0.5}
                                        color="success"
                                        size="small"
                                        decimals={1}
                                        disabled={disabledWithoutSemiValidated}
                                    />
                                }
                            >
                                <LabelValue
                                    title={capitalizeFirstLetter(t('activityOf'))}
                                    value={formatName(
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyTempWorker.activityId)?.reportId.userId
                                            .firstName || '',
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyTempWorker.activityId)?.reportId.userId
                                            .lastName || ''
                                    )}
                                />
                                {dailyTempWorker.unitPrice !== undefined && dailyTempWorker.unitPrice !== null && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('unitPrice'))}
                                        value={`${dailyTempWorker.unitPrice} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                    />
                                )}
                                {!!dailyTempWorker.tempWorkerTypeId.codeProC && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('companyCodeProC'))}
                                        value={dailyTempWorker.tempWorkerTypeId.codeProC}
                                    />
                                )}
                            </ReportItemCard>
                        ))}
                </SectionCard>
                {/* SubContractors */}
                {/*<SectionCard
                    title={capitalizeFirstLetter(t('subContractors'))}
                    iconTitleName="handshake"
                    onAdd={() => {
                        dailySubContractorModal.setTrue();
                        setSelectedDailySubContractor(null);
                    }}
                    disabled={disabledWithoutSemiValidated}
                >
                    {dataSiteReport?.dailySubContractors
                        .sort((a, b) => {
                            const typeA = a.subContractorTypeId.designation;
                            const typeB = b.subContractorTypeId.designation;
                            return typeA.localeCompare(typeB);
                        })
                        .map((dailySubContractor) => (
                            <ReportItemCard
                                key={dailySubContractor._id}
                                title={dailySubContractor.subContractorTypeId.designation}
                                onEdit={() => {
                                    dailySubContractorModal.setTrue();
                                    setSelectedDailySubContractor(dailySubContractor);
                                }}
                                onDelete={() => deleteDailySubContractor({ id: dailySubContractor._id })}
                                priceText={
                                    dailySubContractor.price && dailySubContractor.includedInExpenses ? `${dailySubContractor.price}` : undefined
                                }
                                disabled={disabledWithoutSemiValidated}
                            >
                                <ToggleLine
                                    checked={!!dailySubContractor.includedInExpenses}
                                    onChange={(checked) =>
                                        updateDailySubContractor({ id: dailySubContractor._id, body: { includedInExpenses: checked } })
                                    }
                                    title={capitalizeFirstLetter(t('includedInExpenses'))}
                                    disabled={disabledWithoutSemiValidated}
                                />
                            </ReportItemCard>
                        ))}
                </SectionCard>*/}
                {/* Supplies */}
                <SectionCard
                    title={capitalizeFirstLetter(t('supplies'))}
                    iconTitleName="inventory2"
                    onAdd={() => {
                        dailySupplyModal.setTrue();
                        setSelectedDailySupply(null);
                    }}
                    disabled={disabledWithoutSemiValidated}
                    totalPrice={
                        !!dataSiteReport?.dailySupplies.length
                            ? dataSiteReport?.dailySupplies.reduce((acc, dailySupply) => {
                                  return acc + (dailySupply.unitPrice ?? 0) * (dailySupply.quantity ?? 0);
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.dailySupplies
                        .sort((a, b) => a.companyId.designation.localeCompare(b.companyId.designation))
                        .map((dailySupply) => (
                            <ReportItemCard
                                key={dailySupply._id}
                                title={`${dailySupply.designation} - ${dailySupply.companyId.designation}`}
                                onEdit={() => {
                                    dailySupplyModal.setTrue();
                                    setSelectedDailySupply(dailySupply);
                                }}
                                onDelete={() => deleteDailySupply(dailySupply._id)}
                                disabled={disabledWithoutSemiValidated}
                                majorAction={
                                    <DisplayBox
                                        text={formatNumber(dailySupply.quantity, t('locale'), 2)}
                                        unit={t(getSupplyUnitLabel(dailySupply.unit))}
                                        disabled={disabledWithoutSemiValidated}
                                    />
                                }
                                price={dailySupply.unitPrice ? dailySupply.unitPrice * dailySupply.quantity : undefined}
                                unitPrice={dailySupply.unitPrice}
                            >
                                <LabelValue
                                    title={capitalizeFirstLetter(t('activityOf'))}
                                    value={formatName(
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailySupply.activityId)?.reportId.userId
                                            .firstName || '',
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailySupply.activityId)?.reportId.userId
                                            .lastName || ''
                                    )}
                                />
                                {dailySupply.unitPrice !== undefined && dailySupply.unitPrice !== null && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('unitPrice'))}
                                        value={`${dailySupply.unitPrice} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                    />
                                )}
                                {!!dailySupply.orderNumber && (
                                    <LabelValue title={capitalizeFirstLetter(t('orderNumber'))} value={dailySupply.orderNumber} />
                                )}
                                {!!dailySupply.companyId.codeProC && (
                                    <LabelValue title={capitalizeFirstLetter(t('companyCodeProC'))} value={dailySupply.companyId.codeProC} />
                                )}
                                {!!dailySupply.observations && (
                                    <LabelValue title={capitalizeFirstLetter(t('observations'))} value={dailySupply.observations} />
                                )}
                            </ReportItemCard>
                        ))}
                </SectionCard>
                <DailyTrucksSection
                    currentDate={currentDate}
                    dailyTrucks={dataSiteReport?.dailyTrucks || []}
                    rides={dataSiteReport?.dailyManagerTruckRides || []}
                    title={capitalizeFirstLetter(t('truckRenters'))}
                    siteName={dataSiteReport?.site.name || ''}
                    activities={dataSiteReport?.activities || []}
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    disabled={disabledWithoutSemiValidated}
                    isOwnCompany={false}
                />
                <DailyTrucksSection
                    currentDate={currentDate}
                    dailyTrucks={dataSiteReport?.dailyTrucks || []}
                    rides={dataSiteReport?.dailyDriversTruckRides || []}
                    title={capitalizeFirstLetter(t('trucks'))}
                    siteName={dataSiteReport?.site.name || ''}
                    activities={dataSiteReport?.activities || []}
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    disabled={disabled}
                    isOwnCompany
                />

                <SectionCard
                    title={capitalizeFirstLetter(t('wastes'))}
                    iconTitleName="landscape"
                    disabled={disabled}
                    onAdd={() => {
                        dailyWasteModal.setTrue();
                        setSelectedDailyWaste(null);
                    }}
                    totalPrice={
                        !!dataSiteReport?.dailyWastes.length
                            ? dataSiteReport?.dailyWastes.reduce((acc, dailyWaste) => {
                                  return acc + (dailyWaste.unitPrice ?? 0) * (dailyWaste.quantity ?? 0);
                              }, 0)
                            : undefined
                    }
                >
                    {dataSiteReport?.dailyWastes
                        .sort((a, b) => {
                            const directionA = a.direction ?? Infinity;
                            const directionB = b.direction ?? Infinity;
                            if (directionA !== directionB) return directionA - directionB;
                            const natureA = a.loadNatureId?.designation || '';
                            const natureB = b.loadNatureId?.designation || '';
                            if (natureA !== natureB) return natureA.localeCompare(natureB);

                            const companyA = a.companyId?.designation || '';
                            const companyB = b.companyId?.designation || '';
                            if (companyA !== companyB) return companyA.localeCompare(companyB);

                            const placeA = a.siteId?.name || a.quarryId?.designation || '';
                            const placeB = b.siteId?.name || b.quarryId?.designation || '';
                            return placeA.localeCompare(placeB);
                        })
                        .map((dailyWaste) => (
                            <ReportItemCard
                                key={dailyWaste._id}
                                title={`${capitalizeFirstLetter(t(getWasteDirectionLabel(dailyWaste.direction)))} - ${dailyWaste.loadNatureId.designation} - ${dailyWaste.companyId?.designation ? `${dailyWaste.companyId?.designation} - ` : ''}${dailyWaste.siteId?.name || dailyWaste.quarryId?.designation || ''}`}
                                onEdit={() => {
                                    dailyWasteModal.setTrue();
                                    setSelectedDailyWaste(dailyWaste);
                                }}
                                onDelete={() => deleteDailyWaste(dailyWaste._id)}
                                disabled={disabled}
                                majorAction={
                                    <DisplayBox text={formatNumber(dailyWaste.quantity, t('locale'), 2)} unit={t('tons')} disabled={disabled} />
                                }
                                price={
                                    dailyWaste.unitPrice !== undefined && dailyWaste.unitPrice !== null
                                        ? dailyWaste.unitPrice * (dailyWaste.quantity ?? 0)
                                        : undefined
                                }
                                unitPrice={dailyWaste.unitPrice}
                                extraInfo={
                                    <>
                                        {dailyWaste.direction !== wasteDirectionEnum.IN && (
                                            <>
                                                {!dailyWaste.wasteCodeId?.designation ? (
                                                    <Typography color="error">{t('wasteCodeMissing')}</Typography>
                                                ) : (
                                                    <Typography>{dailyWaste.wasteCodeId?.designation}</Typography>
                                                )}
                                            </>
                                        )}
                                        <Typography width={80} textAlign={'right'}>
                                            {
                                                (!!dailyWaste.companyId?.isOwnCompany
                                                    ? dataSiteReport.dailyDriversTruckRides
                                                    : dataSiteReport.dailyManagerTruckRides
                                                ).filter(
                                                    (ride) =>
                                                        ride.truckId.truckCompanyId._id === dailyWaste.companyId?._id &&
                                                        (dailyWaste.direction === wasteDirectionEnum.IN
                                                            ? ride.departureLoadNatureId?._id === dailyWaste.loadNatureId._id &&
                                                              (ride.departureSiteId?._id === dailyWaste.siteId?._id ||
                                                                  ride.departureQuarryId?._id === dailyWaste.quarryId?._id)
                                                            : ride.arrivalLoadNatureId?._id === dailyWaste.loadNatureId._id &&
                                                              (ride.arrivalSiteId?._id === dailyWaste.siteId?._id ||
                                                                  ride.arrivalQuarryId?._id === dailyWaste.quarryId?._id))
                                                ).length
                                            }{' '}
                                            {t('ride(s)')}
                                        </Typography>
                                    </>
                                }
                            >
                                <LabelValue
                                    title={capitalizeFirstLetter(t('activityOf'))}
                                    value={formatName(
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyWaste.activityId)?.reportId.userId
                                            .firstName || '',
                                        dataSiteReport?.activities?.find((activity) => activity._id === dailyWaste.activityId)?.reportId.userId
                                            .lastName || ''
                                    )}
                                />
                                {dailyWaste.unitPrice !== undefined && dailyWaste.unitPrice !== null && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('unitPrice'))}
                                        value={`${formatNumber(dailyWaste.unitPrice, t('locale'), 2)} ${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}`}
                                    />
                                )}
                                {dailyWaste.direction !== wasteDirectionEnum.IN && !!dailyWaste.wasteCodeId && (
                                    <LabelValue
                                        title={capitalizeFirstLetter(t('waste'))}
                                        value={`${dailyWaste.wasteCodeId.designation} - ${dailyWaste.wasteCodeId.description}`}
                                    />
                                )}
                            </ReportItemCard>
                        ))}
                </SectionCard>
                <SectionCard
                    title={capitalizeFirstLetter(t('observations'))}
                    iconTitleName="notes"
                    disabled={disabled}
                    hideTotalPrice
                    extraActions={<IconButton iconName="edit" onClick={globalObservationsModal.setTrue} />}
                >
                    <Typography variant="body1" whiteSpace="pre-wrap" sx={{ px: 4 }}>
                        {dataSiteDailyReport?.globalObservations}
                    </Typography>
                </SectionCard>
            </ScrollPageContainer>
            {activityModal.value && (
                <ManageValidationDailyActivityModal
                    open={activityModal.value}
                    handleClose={activityModal.setFalse}
                    activity={selectedActivity}
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                    siteId={dataSiteReport?.site._id || ''}
                    currentDate={currentDate}
                />
            )}
            {activityTransferModal.value && (
                <ActivityResourceTransferModal
                    open={activityTransferModal.value}
                    handleClose={activityTransferModal.setFalse}
                    activity={selectedActivity}
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                    activities={
                        dataSiteReport?.activities
                            .filter(
                                (activity) =>
                                    activity.activityTypeId.type ===
                                        dataSiteReport?.activities.find((a) => a._id === selectedActivity?._id)?.activityTypeId.type &&
                                    activity._id !== selectedActivity?._id
                            )
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    dailyMachines={dataSiteReport?.dailyMachines.filter((dm) => dm.activityId === selectedActivity?._id) || []}
                    dailyTools={dataSiteReport?.dailyTools.filter((dt) => dt.activityId === selectedActivity?._id) || []}
                    dailyRentals={dataSiteReport?.dailyRentals.filter((dr) => dr.activityId === selectedActivity?._id) || []}
                    dailyTempWorkers={dataSiteReport?.dailyTempWorkers.filter((dtw) => dtw.activityId === selectedActivity?._id) || []}
                    dailySubContractors={dataSiteReport?.dailySubContractors.filter((dsc) => dsc.activityId === selectedActivity?._id) || []}
                    dailySupplies={dataSiteReport?.dailySupplies.filter((ds) => ds.activityId === selectedActivity?._id) || []}
                    dailyTrucks={dataSiteReport?.dailyTrucks.filter((dt) => dt.activityId === selectedActivity?._id) || []}
                />
            )}
            {dailyMachineModal.value && (
                <ManageValidationDailyMachineModal
                    open={dailyMachineModal.value}
                    handleClose={dailyMachineModal.setFalse}
                    dailyMachine={selectedDailyMachine}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type !== activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {dailyToolModal.value && (
                <ManageValidationDailyToolModal
                    open={dailyToolModal.value}
                    handleClose={dailyToolModal.setFalse}
                    dailyTool={selectedDailyTool}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type !== activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {dailyRentalModal.value && (
                <ManageValidationDailyRentalModal
                    open={dailyRentalModal.value}
                    handleClose={dailyRentalModal.setFalse}
                    dailyRental={selectedDailyRental}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type !== activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {dailyTempWorkerModal.value && (
                <ManageValidationDailyTempWorkerModal
                    open={dailyTempWorkerModal.value}
                    handleClose={dailyTempWorkerModal.setFalse}
                    dailyTempWorker={selectedDailyTempWorker}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type !== activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {dailySubContractorModal.value && (
                <ManageValidationDailySubContractorModal
                    open={dailySubContractorModal.value}
                    handleClose={dailySubContractorModal.setFalse}
                    dailySubContractor={selectedDailySubContractor}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type !== activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {dailySupplyModal.value && (
                <ManageValidationDailySupplyModal
                    open={dailySupplyModal.value}
                    handleClose={dailySupplyModal.setFalse}
                    dailySupply={selectedDailySupply}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type !== activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {dailyWasteModal.value && (
                <ManageValidationDailyWasteModal
                    open={dailyWasteModal.value}
                    handleClose={dailyWasteModal.setFalse}
                    dailyWaste={selectedDailyWaste}
                    activities={
                        dataSiteReport?.activities
                            .filter((activity) => activity.activityTypeId.type === activityTypeEnum.TRUCK)
                            .map((activity) => ({
                                label: `${formatName(activity.reportId.userId.firstName, activity.reportId.userId.lastName)} - ${capitalizeFirstLetter(t(activity.activityTypeId.designation))}`,
                                value: activity._id,
                            })) || []
                    }
                    refetch={() => refetchSiteReport({ cancelRefetch: true })}
                    siteName={dataSiteReport?.site.name || ''}
                />
            )}
            {globalObservationsModal.value && (
                <ManageValidationDailyObservationsModal
                    open={globalObservationsModal.value}
                    handleClose={globalObservationsModal.setFalse}
                    siteName={dataSiteReport?.site.name || ''}
                    refetch={refetchSiteDailyReport}
                    siteDailyReport={dataSiteDailyReport}
                    date={currentDate.toISOString()}
                    siteId={siteId}
                />
            )}
        </PrimaryBox>
    );
};

export default SiteValidationPage;
